/**
 * Landing 真錄音示範的薄 API wrapper。後端契約:
 * POST /api/v1/anonymous-demos(multipart `audio`)→ 201
 * { demo: { text, durationSecs } }。
 *
 * 走集中式 PUBLIC client(#223):匿名端點,不送 bearer / X-Active-Context,
 * 即使訪客剛好已登入。錯誤統一由 createApiClient 丟 ApiError,這裡包回
 * LandingDemoApiError 以保留 UI 對 code(rate_limited…)的辨識性。
 */

import { ApiError } from '@kiao-voice/api-client'
import { createWebPublicApiClient, getWebPublicApiClient } from '../../lib/web-api-client'

export type LandingDemoErrorCode =
  | 'audio_file_required'
  | 'audio_too_long'
  | 'unsupported_format'
  | 'rate_limited'
  | 'unknown'

/** Same payload as the desktop anonymous demo — same endpoint. */
import type { AnonymousDemoResult } from '@kiao-voice/api-contract/anonymous-demo'

type LandingDemoResult = AnonymousDemoResult
export type { LandingDemoResult }

export class LandingDemoApiError extends Error {
  readonly status: number
  readonly code: LandingDemoErrorCode
  /** API 的人類可讀訊息(英文)— 只供除錯,不要直接渲染。 */
  readonly serverMessage: string | null

  constructor(init: {
    message: string
    status: number
    code: LandingDemoErrorCode
    serverMessage?: string | null
  }) {
    super(init.message)
    this.name = 'LandingDemoApiError'
    this.status = init.status
    this.code = init.code
    this.serverMessage = init.serverMessage ?? null
  }
}

interface SubmitOpts {
  /** Test seam — inject a mock fetch (routed through the public client). */
  fetchFn?: typeof fetch
}

/**
 * Upload deadline. On a bad/half-open network the request can hang with no
 * response and no close, leaving the demo card stuck in `processing` forever.
 * The shared `fetchWithTimeout` (createApiClient) aborts and rejects at this
 * bound so the UI can leave the spinner. 60s matches the desktop batch
 * transcription timeout (TRANSCRIPTION_REQUEST_TIMEOUT_MS) — a short demo clip's
 * STT + enhancement finishes well within it, so this only trips on a genuine
 * network stall, not on a slow-but-working request.
 */
const DEMO_UPLOAD_TIMEOUT_MS = 60_000

function toLandingDemoError(error: ApiError): LandingDemoApiError {
  const body = error.body as { error?: unknown; message?: unknown } | null | undefined
  let code: LandingDemoErrorCode = error.status === 429 ? 'rate_limited' : 'unknown'
  let serverMessage: string | null = null
  if (typeof body?.error === 'string') code = body.error as LandingDemoErrorCode
  if (typeof body?.message === 'string') serverMessage = body.message
  return new LandingDemoApiError({
    message: serverMessage ?? `Request failed (HTTP ${error.status}).`,
    status: error.status,
    code,
    serverMessage,
  })
}

export async function submitAnonymousDemo(
  audio: Blob,
  opts: SubmitOpts = {}
): Promise<LandingDemoResult> {
  const client = opts.fetchFn ? createWebPublicApiClient(opts.fetchFn) : getWebPublicApiClient()

  const formData = new FormData()
  const fileName = audio.type.includes('mp4') ? 'recording.mp4' : 'recording.webm'
  formData.append('audio', audio, fileName)

  try {
    const body = await client.upload<{ demo: LandingDemoResult }>(
      '/api/v1/anonymous-demos',
      formData,
      { timeoutMs: DEMO_UPLOAD_TIMEOUT_MS }
    )
    return body.demo
  } catch (error) {
    if (error instanceof ApiError) {
      throw toLandingDemoError(error)
    }
    // Network failure etc. — propagate (begin()'s caller falls back).
    throw error
  }
}
